import type { CatalogSnapshot, CategoryItem, LiveChannel, SeriesItem, VodItem } from '../types';
import { messageFromFetchFailure } from '../utils/playbackErrors';

export interface M3uParseOptions {
  sourceId: string;
  defaultName?: string;
}

/** Parse EXTINF attributes like tvg-logo="..." group-title="News" */
function parseExtinfAttributes(line: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([a-zA-Z0-9_-]+)="([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line)) !== null) {
    attrs[m[1].toLowerCase()] = m[2];
  }
  return attrs;
}

/** Extract display name after last comma in EXTINF line */
function parseExtinfTitle(line: string): string {
  const comma = line.lastIndexOf(',');
  if (comma === -1) return line.replace(/^#EXTINF[^:]*:/i, '').trim();
  return line.slice(comma + 1).trim();
}

function guessKind(group: string, title: string): 'live' | 'vod' | 'series' {
  const g = `${group} ${title}`.toLowerCase();
  if (/\b(s\d{1,2}e\d{1,2}|season\s*\d+|episode\s*\d+)\b/.test(g)) return 'series';
  if (/\b(movie|vod|films|cinema|4k)\b/.test(g)) return 'vod';
  return 'live';
}

/**
 * Minimal M3U parser: supports #EXTINF and URL lines, group-title categories.
 */
export function parseM3uText(text: string, options: M3uParseOptions): CatalogSnapshot {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const groupNames = new Map<string, string>();
  const liveChannels: LiveChannel[] = [];
  const vodItems: VodItem[] = [];
  const seriesItems: SeriesItem[] = [];
  let order = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line.startsWith('#EXTINF')) continue;
    const urlLine = lines[i + 1];
    if (!urlLine || urlLine.startsWith('#')) continue;
    const attrs = parseExtinfAttributes(line);
    const title = attrs.title || parseExtinfTitle(line);
    const group = attrs['group-title'] || 'General';
    const logo = attrs['tvg-logo'] || attrs.logo;
    const cid = `grp:${group}`;

    if (!groupNames.has(cid)) {
      groupNames.set(cid, group);
    }

    const kind = guessKind(group, title);
    const id = `m3u-${order}`;
    order += 1;

    if (kind === 'vod') {
      vodItems.push({
        id,
        categoryId: cid,
        name: title,
        poster: logo,
        streamUrl: urlLine,
      });
    } else if (kind === 'series') {
      seriesItems.push({
        id,
        categoryId: cid,
        name: title,
        poster: logo,
        streamUrl: urlLine,
      });
    } else {
      liveChannels.push({
        id,
        categoryId: cid,
        name: title,
        logo,
        streamUrl: urlLine,
      });
    }
    i += 1;
  }

  const liveCategories: CategoryItem[] = [];
  const vodCategories: CategoryItem[] = [];
  const seriesCategories: CategoryItem[] = [];

  groupNames.forEach((name, id) => {
    if (liveChannels.some((c) => c.categoryId === id)) {
      liveCategories.push({ id, name });
    }
    if (vodItems.some((c) => c.categoryId === id)) {
      vodCategories.push({ id, name });
    }
    if (seriesItems.some((c) => c.categoryId === id)) {
      seriesCategories.push({ id, name });
    }
  });

  // If everything landed in one bucket, ensure at least one category
  if (!liveCategories.length && liveChannels.length) {
    liveCategories.push({ id: 'all', name: options.defaultName || 'All channels' });
    liveChannels.forEach((c) => { c.categoryId = 'all'; });
  }

  return {
    sourceId: options.sourceId,
    liveCategories: liveCategories.length ? liveCategories : [{ id: 'all', name: 'All' }],
    liveChannels,
    vodCategories: vodCategories.length ? vodCategories : [],
    vodItems,
    seriesCategories: seriesCategories.length ? seriesCategories : [],
    seriesItems,
    seriesDetails: {},
  };
}

/** Large IPTV lists can be tens of MB; cap browser import to avoid freezing / OOM. */
const M3U_MAX_BYTES = 45 * 1024 * 1024;

/** Default max wait for playlist URL fetch + body (user can cancel earlier via {@link fetchM3uFromUrl} `signal`). */
export const M3U_DEFAULT_FETCH_TIMEOUT_MS = 90_000;

function isAbortError(e: unknown): boolean {
  const name = e instanceof Error ? e.name : '';
  const msg = e instanceof Error ? e.message : String(e);
  return name === 'AbortError' || /aborted|AbortError/i.test(msg);
}

export interface FetchM3uOptions {
  /** When aborted (e.g. user clicks Cancel), fetch stops with “Loading cancelled.” */
  signal?: AbortSignal;
  /** Max time for headers + body; default {@link M3U_DEFAULT_FETCH_TIMEOUT_MS}. */
  timeoutMs?: number;
}

export async function fetchM3uFromUrl(url: string, options?: FetchM3uOptions): Promise<string> {
  const trimmed = url.trim();
  if (!trimmed) throw new Error('Playlist URL is empty.');

  const parent = options?.signal;
  const timeoutMs = options?.timeoutMs ?? M3U_DEFAULT_FETCH_TIMEOUT_MS;

  const combined = new AbortController();
  const onParentAbort = () => combined.abort();
  if (parent) {
    if (parent.aborted) combined.abort();
    else parent.addEventListener('abort', onParentAbort);
  }
  const tid = setTimeout(() => combined.abort(), timeoutMs);

  const cleanup = () => {
    clearTimeout(tid);
    if (parent) parent.removeEventListener('abort', onParentAbort);
  };

  try {
    let res: Response;
    try {
      res = await fetch(trimmed, {
        method: 'GET',
        signal: combined.signal,
        cache: 'no-store',
        credentials: 'omit',
        headers: {
          Accept: 'application/vnd.apple.mpegurl, application/x-mpegURL, audio/x-mpegurl, text/plain, */*',
        },
      });
    } catch (e) {
      if (!isAbortError(e)) {
        throw new Error(messageFromFetchFailure(e));
      }
      if (parent?.aborted) {
        throw new Error('Loading cancelled.');
      }
      throw new Error(
        `Playlist fetch timed out after ${Math.round(timeoutMs / 1000)}s. The host may be slow, the file very large, or the server may be returning errors (e.g. Cloudflare HTTP 520). Download the .m3u and use **Upload M3U**, or try again later.`,
      );
    }

    if (!res.ok) {
      const cf =
        res.status === 520 || res.status === 521 || res.status === 522 || res.status === 524
          ? ' The origin or CDN had a problem (common with free playlist hosts). Try again later, open the URL in a new tab to verify it, or download the file and use **Upload M3U**.'
          : '';
      throw new Error(`Playlist HTTP ${res.status}. Check the URL or try **Upload M3U** instead.${cf}`);
    }

    const cl = res.headers.get('content-length');
    if (cl) {
      const n = Number(cl);
      if (Number.isFinite(n) && n > M3U_MAX_BYTES) {
        throw new Error(
          `Playlist is too large (${Math.ceil(n / 1024 / 1024)} MB; max ${M3U_MAX_BYTES / 1024 / 1024} MB in the browser). Use **Upload M3U** with a trimmed file or a smaller list.`,
        );
      }
    }

    let buf: ArrayBuffer;
    try {
      buf = await res.arrayBuffer();
    } catch (e) {
      if (!isAbortError(e)) throw e;
      if (parent?.aborted) {
        throw new Error('Loading cancelled.');
      }
      throw new Error(
        `Playlist fetch timed out after ${Math.round(timeoutMs / 1000)}s while reading the response. Try **Upload M3U** or a smaller playlist.`,
      );
    }
    if (buf.byteLength > M3U_MAX_BYTES) {
      throw new Error(
        `Downloaded playlist exceeds ${M3U_MAX_BYTES / 1024 / 1024} MB. Use **Upload M3U** with a trimmed file.`,
      );
    }

    const text = new TextDecoder('utf-8', { fatal: false }).decode(buf);
    const t = text.trimStart();
    if (!t.includes('#EXTM3U') && !t.includes('#EXTINF')) {
      const probe = t.slice(0, 400).toLowerCase();
      if (probe.includes('cloudflare') || probe.includes('error code') || probe.includes('<!doctype')) {
        throw new Error(
          'The URL did not return M3U content (it looks like an HTML error page). The server may be overloaded (HTTP 520), blocking scrapers, or the link may be wrong. Open the URL in a normal browser tab to verify, try again later, or download the file and use **Upload M3U**.',
        );
      }
      throw new Error('Invalid playlist: expected M3U content (#EXTM3U / #EXTINF).');
    }
    return text;
  } finally {
    cleanup();
  }
}
